// router/routes.js - Framework7 v9 + Vue 3
import {
  CLUB_MANIFEST,
  canBrowseSupplierShell,
  clubLandingPath,
  engagementFor,
  enumerateClubs,
  launchLandingPath,
  ONBOARDING_ENTRY,
  supplierStageOf,
} from '@/clubs/manifest.js';
import { collectorHoldActive } from '@/collector/session.js';
import ClubChoiceScreen from '@/components/onboarding/ClubChoiceScreen.vue';
import BasePage from '@/pages/BasePage.vue';
// EAGER (not asyncComponent): /collector/ and /collector/shops/:supplierId/ are
// initial-URL eligible — the launch handoff lands a collector on /collector/,
// and a reload in the market (weak signal, a tab brought back) makes either the
// boot URL; asyncComponent would paint "[object Promise]" on that first render
// (playbook Section 5 row 16).
import CollectorDayPage from '@/pages/CollectorDayPage.vue';
import CollectorShopPage from '@/pages/CollectorShopPage.vue';
// EAGER (not asyncComponent): /issues/ and /issues/:issueId/ are initial-URL
// eligible — the ItemIssueNeedsDecision notification deep-links straight to
// /issues/{id}/, so a push tap / PWA cold-launch can make it the boot URL, and
// asyncComponent would paint the literal "[object Promise]" on that first render
// (playbook Section 5 row 16). Same reasoning as OrderBoxTotalsPage / SupplierBoxDetail.
import DecisionsInboxPage from '@/pages/DecisionsInboxPage.vue';
import IssueDecisionPage from '@/pages/IssueDecisionPage.vue';
import LaunchPage from '@/pages/LaunchPage.vue';
import LoginPage from '@/pages/LoginPage.vue';
import MySuggestionsPage from '@/pages/MySuggestionsPage.vue';
import NotFoundPage from '@/pages/NotFoundPage.vue';
import OrderBoxTotalsPage from '@/pages/OrderBoxTotalsPage.vue';
// EAGER (not asyncComponent): /orders/box/:boxId/ is initial-URL eligible —
// tips-wave P3-A points the out-of-stock / suggestion-outcome / ready-missing-
// items pushes straight here (owner answer W-K), so a push tap / PWA
// cold-launch can make it the boot URL, and asyncComponent would paint the
// literal "[object Promise]" on that first render (playbook Section 5 row 16).
// Same reasoning as OrderBoxTotalsPage right above it — it is the SAME
// component OrdersPage.vue already uses as the `/orders/` tab, just reached
// as a standalone pushed route with `meta.hideTabbar: true` instead of a tab.
import OrdersPage from '@/pages/OrdersPage.vue';
// EAGER (not asyncComponent): /poola/last/ and /suppliers/poola/last/ are
// initial-URL eligible — a member reading the last Poola who reloads makes it the
// boot URL, and asyncComponent would paint the literal "[object Promise]" on that
// first render (playbook Section 5 row 16). Same reasoning as /poola/ itself.
import PoolaLastRunPage from '@/pages/PoolaLastRunPage.vue';
import PoolaPage from '@/pages/PoolaPage.vue';
// EAGER (not asyncComponent): /posts/:id/ is initial-URL eligible — it is the
// deep-link target for the `product.unavailable` fan-out notification, so a push
// tap / PWA cold-launch can make it the boot URL, and asyncComponent would paint
// the literal "[object Promise]" on that first render (playbook Section 5 row 16).
// Same reasoning as /issues/:issueId/ and /suppliers/box/:boxId/.
import PostDetailPage from '@/pages/PostDetailPage.vue';
// EAGER (not asyncComponent): /referral/ is initial-URL eligible — a member
// sitting on the invite screen who refreshes the PWA makes it the cold-boot URL,
// and asyncComponent would paint the literal "[object Promise]" on that first
// render (playbook Section 5 row 16). Same reasoning as /poola/ and OrderBoxTotalsPage.
// Guards still fire with eager component: in F7 v9 on soft navigations.
import ReferralPage from '@/pages/ReferralPage.vue';
// EAGER (not asyncComponent): /suggestions/ is initial-URL eligible — a member
// on «اقتراحاتي» who refreshes the PWA makes it the cold-boot URL, and
// asyncComponent would paint the literal "[object Promise]" (playbook Section 5
// row 16). Same reasoning as /referral/ above.
import SupplierBasePage from '@/pages/SupplierBasePage.vue';
// EAGER (not asyncComponent): /suppliers/box/:boxId/ is initial-URL eligible — a
// push / PWA cold-launch deep-link to the supplier order detail can make it the
// boot URL, and asyncComponent would paint the literal "[object Promise]" on that
// first render (playbook Section 5 row 16). Same reasoning as OrderBoxTotalsPage's
// order-box-totals route. Guards still fire with eager component: in F7 v9.
import SupplierBoxDetailPage from '@/pages/SupplierBoxDetailPage.vue';
// EAGER (not asyncComponent): /tadbeesa/ is initial-URL eligible — a member who
// reloads the club's shelf page makes it the boot URL, and asyncComponent would
// paint the literal "[object Promise]" on that first render (playbook Section 5
// row 16). Same reasoning as /posts/:id/ and /referral/.
import TadbeesaPage from '@/pages/TadbeesaPage.vue';
import { useAuthStore } from '@/stores/authStore';
import { supplierPoolaTwin } from '@/utils/poolaPaths.js';

/**
 * Onboarding entry path. A `needsRegistration` user (authed, no membership)
 * lands here — the welcome/club-choice screen (ClubChoiceScreen). Single
 * constant so the three guards below and the App.vue boot pre-empt can't drift
 * on where membership-less users go. Replaces the old `/seller-registration/`
 * target (kept as a redirect to this for legacy URLs/bookmarks). Defined in
 * clubs/manifest.js (the boot pre-empts read it without the route table) and
 * re-exported here for the existing importers.
 */
export { ONBOARDING_ENTRY };

/**
 * The supplier-stage reading every guard below shares — the stage on the
 * persisted profile, with `isSupplier` as the fallback for a session cached
 * before the backend sent it (clubs/manifest.js `supplierStageOf`).
 */
function stageCapabilities(authStore) {
  return { supplierStage: authStore.supplierStage, isSupplier: authStore.isSupplier };
}

/**
 * CRITICAL: Framework7 Route Guards receive a SINGLE context object
 * Per official docs: https://framework7.io/docs/routes.html#route-callback-context
 *
 * Context object contains: { app, to, from, resolve, reject, router, direction }
 *
 * DO NOT use: function(to, from, resolve, reject) { const router = this; }
 * CORRECT:    function({ to, from, resolve, reject, router }) { }
 */

/**
 * Route Guard: Require authentication ONLY (no membership, no role).
 *
 * For surfaces every authenticated user must reach regardless of membership
 * state — including PENDING / awaiting-approval members who have NO active
 * membership yet. The Poola teaser (`/poola/`) is the first such surface: a
 * pre-launch marketing page shown to active sellers, active suppliers, AND
 * pending members alike (locked decision: requireAuth, NOT requireMembership —
 * see _bmad-output/planning-artifacts/poola-teaser-prompts-copyable-2026-06-21.md).
 *
 * Distinct from requireMembership (bounces membership-less users to onboarding)
 * and requireSupplier (adds the supplier-role gate). This guard stops ONLY at the
 * authentication boundary; the guarded page must not assume an active membership
 * (PoolaPage gates its per-user data on hasActiveMembership internally).
 */
function requireAuth({ to, from: _from, resolve, reject, router }) {
  const authStore = useAuthStore();

  console.log('[Guard:requireAuth]', {
    path: to.path,
    isAuthenticated: authStore.isAuthenticated,
  });

  if (!authStore.isAuthenticated) {
    console.log('[Guard] Not authenticated, redirecting to login');
    reject();
    router.navigate('/login/', {
      clearPreviousHistory: true,
    });
    return;
  }

  resolve();
}

/**
 * Route Guard: the two SELLER Poola routes (/poola/, /poola/last/).
 *
 * requireAuth, plus one rewrite: anyone supplier-side (any stage but `none`)
 * goes to the supplier twin (/suppliers/poola/, /suppliers/poola/last/), where
 * the route resolves to the suppliers club — so he never gets the seller
 * tabbar and the seller deposit banner (supplier-showroom P2-A). A plain push,
 * not a cleared history: the back button still returns to where he tapped.
 * Both routes are eager, so a COLD load skips this guard (row 22); App.vue's
 * `sellerRoutePreempt` does the same rewrite before the view mounts.
 */
function requireAuthSellerPoola(ctx) {
  const authStore = useAuthStore();
  const twin = authStore.isAuthenticated
    ? supplierPoolaTwin(ctx.to.path, stageCapabilities(authStore))
    : null;
  if (twin) {
    console.log('[Guard:requireAuthSellerPoola] supplier on seller Poola, going to', twin);
    ctx.reject();
    ctx.router.navigate(twin);
    return;
  }
  requireAuth(ctx);
}

/**
 * Where a signed-in user with NO club membership belongs: a collector or leader
 * (staff, usually no club) on his day, everyone else on the onboarding entry.
 * Third place of the collector landing rule, next to LoginPage's
 * showSuccessAndNavigate and LaunchPage's landingPath() (all via
 * launchLandingPath). Without it the back button (browser history is on)
 * returned him to /login/ or the club picker.
 */
function membershipLessLanding(authStore) {
  const day = launchLandingPath({ isCollector: authStore.isCollector, hasActiveMembership: false });
  return day === CLUB_MANIFEST.collectors.root ? day : ONBOARDING_ENTRY;
}

/**
 * Route Guard: Require authentication AND active membership
 * Framework7 pattern: resolve() for success, reject() + router.navigate() for redirect
 */
function requireMembership({ to, from: _from, resolve, reject, router }) {
  const authStore = useAuthStore();

  console.log('[Guard:requireMembership]', {
    path: to.path,
    isAuthenticated: authStore.isAuthenticated,
    hasActiveMembership: authStore.hasActiveMembership,
    memberShip: authStore.membership,
    user: authStore.user,
  });

  // Check authentication first
  if (!authStore.isAuthenticated) {
    console.log('[Guard] Not authenticated, redirecting to login');
    reject();
    router.navigate('/login/', {
      clearPreviousHistory: true,
    });
    return;
  }

  // Check active membership
  if (!authStore.hasActiveMembership) {
    console.log('[Guard] No active membership, redirecting to onboarding (or his day, for a collector)');
    reject();
    router.navigate(membershipLessLanding(authStore), {
      clearPreviousHistory: true,
    });
    return;
  }

  // A supplier never belongs in the seller shell. requireSupplier bounces
  // non-suppliers off /suppliers/; this is the symmetric bounce. Without it the
  // club landing is enforced ONLY by the /login/ flip (redirectIfAuthenticated),
  // so any other way into a seller route — the PWA start_url '/', a bookmark, a
  // cached-session cold boot, a direct nav — would strand a supplier in the
  // (for them, empty/erroring) seller shell. Any stage but `none` counts — an
  // `interested` lead goes to his waiting screen, a showroom or active supplier
  // to the supplier shell (clubLandingPath; supplier-showroom P2-A closes the
  // June pending-supplier gap).
  const capabilities = stageCapabilities(authStore);
  const clubs = enumerateClubs(capabilities);
  if (!clubs.includes('sellers')) {
    // Profile is the one surface shared by both shells (same ProfilePage). Map
    // the seller profile URL to its supplier-shell twin so a supplier asking for
    // '/profile/' keeps that intent; every other seller route has no supplier
    // counterpart, so it falls back to the landing (the Wall, or the waiting
    // screen for a lead who cannot open the shell yet).
    const supplierRoot = CLUB_MANIFEST.suppliers.root; // '/suppliers/'
    const landing = clubLandingPath(capabilities);
    const target =
      to.path === '/profile/' && landing === supplierRoot ? `${supplierRoot}profile/` : landing;
    console.log('[Guard] Supplier in seller route, redirecting to', target);
    reject();
    router.navigate(target, {
      clearPreviousHistory: true,
    });
    return;
  }

  // All checks passed
  console.log('[Guard] Access granted to', to.path);
  resolve();
}

/**
 * Route Guard: Require authentication + active membership + a supplier stage
 * that may browse the shell.
 *
 * Mirrors requireMembership and adds the supplier gate: `showroom` (pending,
 * showroom opened — reads only) and `active` pass; an `interested` lead goes to
 * his waiting screen; everyone else (`none`) to their landing. The stage is read
 * from the persisted auth store (set from /users/-actions/profile — see
 * authStore.setSession), so the guard works on cold boot without a probe. The
 * backend remains the real boundary: an ineligible user hitting the wall API
 * gets an empty feed / 403, but the guard keeps them off the screen entirely.
 */
function requireSupplier({ to, from: _from, resolve, reject, router }) {
  const authStore = useAuthStore();

  console.log('[Guard:requireSupplier]', {
    path: to.path,
    isAuthenticated: authStore.isAuthenticated,
    hasActiveMembership: authStore.hasActiveMembership,
    isSupplier: authStore.isSupplier,
    supplierStage: authStore.supplierStage,
  });

  if (!authStore.isAuthenticated) {
    reject();
    router.navigate('/login/', { clearPreviousHistory: true });
    return;
  }

  // A collector previewing one shop (supplier-preview P2-A, L-H) has no club
  // membership as a rule; the preview stands in for it. The stage tier below
  // still runs — the profile says `showroom` under a preview.
  if (!authStore.hasActiveMembership && !authStore.isSupplierPreview) {
    reject();
    router.navigate(ONBOARDING_ENTRY, { clearPreviousHistory: true });
    return;
  }

  const capabilities = stageCapabilities(authStore);
  if (!canBrowseSupplierShell(capabilities)) {
    // `interested` → the waiting screen; `none` → '/home/' (clubLandingPath).
    const target = clubLandingPath(capabilities);
    console.log('[Guard] Supplier shell not open for this stage, redirecting to', target, {
      stage: supplierStageOf(capabilities),
    });
    reject();
    router.navigate(target, { clearPreviousHistory: true });
    return;
  }

  resolve();
}

/**
 * Route Guard: Require authentication + the collector capability.
 *
 * Deliberately NOT requireMembership: most collectors have no club membership
 * (four of six sign in only through Telegram), so the membership tier must not
 * stand between them and their day. `isCollector` comes from the persisted
 * profile (authStore.setSession), so the guard works on cold boot without a
 * probe; the backend stays the real boundary (`collector-plans` answers 403 to
 * anyone else). A signed-in non-collector goes where they belong: their club
 * landing when they are a member, onboarding when they are not.
 *
 * Both collector routes are eager `component:`, so F7 SKIPS this guard on a cold
 * hard-load (playbook Section 5 row 22); App.vue's EAGER_COLLECTOR_PREFIX
 * pre-empt mirrors these three tiers for that path.
 */
export function requireCollector({ to, from: _from, resolve, reject, router }) {
  const authStore = useAuthStore();

  console.log('[Guard:requireCollector]', {
    path: to.path,
    isAuthenticated: authStore.isAuthenticated,
    isCollector: authStore.isCollector,
  });

  if (!authStore.isAuthenticated) {
    // A collector held on his day after an expired login (collector-app P2-B,
    // L-D): signed out, but his day and queue are on the phone and the only
    // way back in is the Telegram re-entry his day page offers.
    if (collectorHoldActive()) {
      resolve();
      return;
    }
    reject();
    router.navigate('/login/', { clearPreviousHistory: true });
    return;
  }

  // While he previews a shop's supplier app (supplier-preview P2-A, L-H) the day
  // must not paint — the shop owner may be holding the phone, and the browser's
  // back button would otherwise reach the shop page. The banner's «اقفل العرض»
  // is the only way back. Cold loads: App.vue's boot rewrite (bootPreempts.js
  // `supplierPreviewPreempt`) covers /collector/* before this guard is skipped.
  if (authStore.isSupplierPreview) {
    console.log('[Guard] Supplier preview active, redirecting to /suppliers/');
    reject();
    router.navigate(CLUB_MANIFEST.suppliers.root, { clearPreviousHistory: true });
    return;
  }

  // `isCollector` rides the persisted profile. A session cached before the
  // field existed carries no value yet (undefined, not false): let it through —
  // the background profile refresh fills it in, and `collector-plans` answers
  // 403 to anyone who is not a collector.
  if (!authStore.isCollector && authStore.user?.isCollector !== undefined) {
    const target = authStore.hasActiveMembership
      ? clubLandingPath(stageCapabilities(authStore))
      : ONBOARDING_ENTRY;
    console.log('[Guard] Not a collector, redirecting to', target);
    reject();
    router.navigate(target, { clearPreviousHistory: true });
    return;
  }

  resolve();
}

/**
 * Route Guard: the club-neutral notification center (/notifications/ and
 * /notifications/preferences/; PWA install / notifications audit 2026-11-03,
 * Section 3.7). requireAuth, then the user's club must have a center at all
 * (`engagementFor(...).notificationsRoot`): a seller, a supplier at any stage
 * and a pending member pass; a collector with no club membership has none (his
 * rail is the collector group) and goes to his day. Not requireMembership: that
 * bounces every supplier to the Wall, so the settings helper and the
 * content-free money push landed him there.
 */
function requireNotificationsRoot(ctx) {
  const authStore = useAuthStore();
  if (!authStore.isAuthenticated) {
    requireAuth(ctx);
    return;
  }
  const { notificationsRoot } = engagementFor({
    ...stageCapabilities(authStore),
    isCollector: authStore.isCollector,
    hasActiveMembership: authStore.hasActiveMembership,
  });
  if (notificationsRoot === null) {
    console.log('[Guard:requireNotificationsRoot] no notification center for this user, going to his day');
    ctx.reject();
    ctx.router.navigate(CLUB_MANIFEST.collectors.root, { clearPreviousHistory: true });
    return;
  }
  ctx.resolve();
}

/**
 * Route Guard: Redirect if already authenticated
 */
function redirectIfAuthenticated({ to, from: _from, resolve, reject, router }) {
  const authStore = useAuthStore();

  console.log('[Guard:redirectIfAuthenticated]', {
    path: to.path,
    isAuthenticated: authStore.isAuthenticated,
    needsRegistration: authStore.needsRegistration,
    memberShip: authStore.membership,
    user: authStore.user,
  });

  // Authenticated with a membership (any state incl. PENDING/BLOCKED) → shell.
  // Club- and stage-aware landing (clubLandingPath): a seller lands on /home/,
  // a showroom or active supplier in the supplier shell, an `interested` lead on
  // his waiting screen. One landing decision, shared with App.vue's boot-time
  // pre-empt via clubLandingPath().
  if (authStore.isAuthenticated && authStore.membership?.id) {
    const landing = clubLandingPath(stageCapabilities(authStore));
    console.log('[Guard] Has membership, going to', landing);
    reject();
    router.navigate(landing, {
      clearPreviousHistory: true,
    });
    return;
  }

  // Authenticated but no membership yet → onboarding (club choice); a collector
  // with no club goes to his day, so back onto /login/ never shows him the form.
  if (authStore.isAuthenticated) {
    console.log('[Guard] Authenticated but no membership, going to onboarding');
    reject();
    router.navigate(membershipLessLanding(authStore), {
      clearPreviousHistory: true,
    });
    return;
  }

  // Not authenticated — allow access to login/registration
  console.log('[Guard] Not authenticated, showing page');
  resolve();
}

/**
 * Route Guard: onboarding pages (the /join/ flow — club choice + form).
 *
 * A member (authenticated + active membership, ANY state incl. PENDING) doesn't
 * belong on the onboarding entry — bounce them to their club shell. Everyone
 * else is allowed through: a `needsRegistration` user (authed, no membership)
 * who must choose a club, and an unauthenticated visitor who hard-loads the
 * public welcome (it carries its own "سجّل دخولك" link).
 *
 * Distinct from `redirectIfAuthenticated` (used on /login/), which SELF-redirects
 * membership-less users to the onboarding entry — wrong for /join/ itself, where
 * those users must simply see the page.
 *
 * NOTE (f7v9-vue3-playbook Section 5 row 22): /join/ is an eager `component:` route
 * whose component renders an <f7-page>, so F7 SKIPS this beforeEnter on a cold
 * hard-load / initial URL. The member-bounce for that path is handled by the
 * App.vue boot-time landing pre-empt (which lists /join/). This guard covers
 * soft navigations into the flow.
 */
function redirectMembersToShell({ to: _to, from: _from, resolve, reject, router }) {
  const authStore = useAuthStore();

  if (authStore.isAuthenticated && authStore.membership?.id) {
    const landing = clubLandingPath(stageCapabilities(authStore));
    console.log('[Guard:redirectMembersToShell] member on onboarding, going to', landing);
    reject();
    router.navigate(landing, {
      clearPreviousHistory: true,
    });
    return;
  }

  // A signed-in collector with no club never sees the club picker.
  if (authStore.isAuthenticated && membershipLessLanding(authStore) === CLUB_MANIFEST.collectors.root) {
    reject();
    router.navigate(CLUB_MANIFEST.collectors.root, {
      clearPreviousHistory: true,
    });
    return;
  }

  resolve();
}

/**
 * IMPORTANT: Per Framework7 docs, beforeEnter does not work properly
 * with component property directly. Use asyncComponent instead.
 * Source: https://framework7.io/docs/routes.html#route-before-enter-leave
 */

export default [
  // ===== PUBLIC ROUTES =====
  {
    path: '/login/',
    id: 'login',
    // EAGER: avoids F7-Vue v9 initial-page render bug where asyncComponent on
    // the initial URL renders "[object Promise]" because <f7-view>'s render
    // passes the async function directly to h() before the Promise resolves
    // (node_modules/framework7-vue/components/view.js).
    component: LoginPage,
    beforeEnter: redirectIfAuthenticated,
    options: {
      transition: 'f7-fade',
      clearPreviousHistory: true,
    },
    meta: {
      hideTabbar: true,
      hideNavbar: true,
    },
  },

  // ===== ONBOARDING ROUTES (registration redesign 2026-06-20) =====
  //
  // /join/ is the welcome / club-choice ENTRY (ClubChoiceScreen) — where every
  // needsRegistration user lands (the three guards above + the App.vue boot
  // pre-empt all route here via ONBOARDING_ENTRY).
  //
  // EAGER `component:` is REQUIRED: /join/ is initial-URL-eligible — a direct
  // hard-load/bookmark, OR the App.vue boot rewrite of /login/ → /join/ for an
  // authed no-membership user. With asyncComponent the F7-Vue <f7-view> render
  // would stringify the import Promise as literal "[object Promise]" on first
  // paint (playbook Section 5 row 16). ClubChoiceScreen is a small screen; eager import
  // is cheap (same trade-off as /login/, /, /launch/, /suppliers/).
  //
  // ClubChoiceScreen renders a real <f7-page>, so F7 SKIPS this beforeEnter on a
  // cold hard-load (Section 5 row 22). The member-bounce for that path is the App.vue
  // boot pre-empt's job (it lists /join/); redirectMembersToShell covers soft
  // navigations into the flow.
  {
    path: '/join/',
    id: 'join',
    component: ClubChoiceScreen,
    beforeEnter: redirectMembersToShell,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
      hideNavbar: true,
    },
  },
  // The membership-request form, carrying the chosen club as ?club=sellers|suppliers.
  // P1-A: MembershipRequestForm — shared by both clubs; the ?club query decides the
  // `role` sent to the unified POST /registrations. Reached by tapping a card on
  // /join/ (a soft nav), NOT initial-URL eligible — asyncComponent is correct
  // (playbook Section 5 rows 16/22).
  {
    path: '/join/form/',
    id: 'join-form',
    asyncComponent: () => import('@/components/onboarding/MembershipRequestForm.vue'),
    beforeEnter: redirectMembersToShell,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },
  // The "under review" confirmation (shown right after a successful submit, when
  // the user is already a PENDING member). Intentionally NOT guarded by
  // redirectMembersToShell — the just-registered user IS a (pending) member and
  // must SEE this screen, not be bounced.
  {
    path: '/join/review/',
    id: 'join-review',
    asyncComponent: () => import('@/components/onboarding/UnderReviewScreen.vue'),
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // The supplier gains page (supplier-messages P2-A): what a supplier gains with the
  // club, read before he applies (club picker card), while he waits, and from his
  // showroom (banner + lock sentence). NO guard on purpose — every stage reads it,
  // and a showroom supplier must not be bounced to the shell. asyncComponent like
  // /join/form/ (soft nav, not initial-URL eligible). App.vue's cold-load pre-empt
  // (AUTH_GATED_PUBLIC_PATHS) is an exact-path list that does not name it, so a
  // member cold-loading it stays.
  {
    path: '/join/gains/',
    id: 'join-gains',
    asyncComponent: () => import('@/components/onboarding/SupplierGainsScreen.vue'),
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ── RETIRED onboarding routes → redirect into the /join/ flow ──────────────
  // Different-path redirects (not trailing-slash normalizers), so placement here
  // is safe — they target /join/, never themselves, so no loop (playbook Section 5 row
  // 20 only constrains loose→strict same-path normalizers). No live code
  // navigates to these (verified). The superseded components —
  // SellerRegistrationForm.vue, SellerWelcomeScreen.vue, SellerFeaturesScreen.vue
  // — are now DEAD; mark them for the cleanup pass (do not delete inline per
  // CLAUDE.md "Agent working rules").
  { path: '/seller-registration/', redirect: ONBOARDING_ENTRY },
  { path: '/seller-welcome/', redirect: ONBOARDING_ENTRY },
  { path: '/seller-features/', redirect: ONBOARDING_ENTRY },

  // ===== MAIN APP ROUTES (require auth + membership) =====
  {
    path: '/home/',
    redirect: '/',
    options: {
      transition: 'f7-fade',
    },
  },
  // (Trailing-slash normalization for tab URLs is handled by the redirect
  // block at the bottom of this file, AFTER all real tab routes — see the
  // long comment there for why redirect placement matters with F7's
  // path-to-regexp v6 matcher. `alias:` on tab definitions does NOT work:
  // F7's flattenRoutes does not lift tab.alias into the matcher list.)
  {
    path: '/',
    // EAGER: same reason as /login/ — initial URL must use `component:`,
    // not `asyncComponent:`, or <f7-view> renders "[object Promise]" before
    // the dynamic import resolves. BasePage is a tiny shell (only mounts
    // <tabbar-navigation>) so eager-importing it costs ~nothing.
    //
    // master: true — COLD-LOAD ORPHAN FIX (playbook Section 5 row 24 + row 17).
    // BasePage intentionally renders NO <f7-page> (row 17, required so the
    // tabbar doesn't collapse). On an initial-URL load,
    // get-router-initial-component.js (framework7-vue) would otherwise
    // PRE-RENDER this eager component into pages.value[0]; router.init() then
    // finds children('.page').length === 0 (no .page rendered by the shell) →
    // falls through to router.navigate() and mounts the shell a SECOND time,
    // leaving the first render as an orphan tabbar. master:true sets
    // isMasterRoute(route)=true → the pre-render block is skipped entirely →
    // pages starts empty → init() takes the navigate() path once → single
    // mount. All master-detail side-effects are inert: masterDetailBreakpoint
    // defaults to 0 (view.js:58); every gating check in navigate.js and
    // back.js is `if (masterDetailBreakpoint > 0 …)` or keys off
    // route.masterRoute (a detail-route flag set by flattenRoutes:235, never
    // set here); page-master CSS class gated the same way (router-class.js:915).
    // Guards still fire: the navigate() path calls processRouteQueue which
    // runs beforeEnter (process-route-queue.js:40-42). Flat tab routes
    // inherit master:true via flattenRoutes extend() — covers all cold loads
    // of /wallet/, /orders/, /profile/ too.
    master: true,
    component: BasePage,
    beforeEnter: requireMembership,
    options: {
      transition: 'f7-fade',
    },
    tabs: [
      // First (default) tab has the same url as the page itself
      {
        path: '/',
        id: 'home',
        asyncComponent: () => import('@/pages/HomePage.vue'),
        beforeEnter: requireMembership,
        options: {
          transition: 'f7-fade',
          clearPreviousHistory: false,
        },
        meta: {
          hideTabbar: false,
        },
      },
      {
        path: '/wallet/',
        id: 'wallet',
        asyncComponent: () => import('@/pages/WalletPage.vue'),
        beforeEnter: requireMembership,
        options: {
          transition: 'f7-flip',
        },
        meta: {
          hideTabbar: false,
        },
      },
      {
        path: '/orders/',
        id: 'orders',
        asyncComponent: () => import('@/pages/OrdersPage.vue'),
        beforeEnter: requireMembership,
        meta: {
          hideTabbar: false,
          transition: 'f7-fade',
        },
      },
      {
        path: '/profile/',
        id: 'profile',
        asyncComponent: () => import('@/pages/ProfilePage.vue'),
        beforeEnter: requireMembership,
        meta: {
          hideTabbar: false,
          transition: 'f7-fade',
        },
      },
    ],
  },

  // NOTE: /settings/ was removed 2026-08-14. It served src/pages/Settings.vue —
  // dev scaffold (English theme + text-direction pickers, "Version: 1.0.0")
  // that no screen ever linked to. It rendered a bare <div class="page-content">
  // with no <f7-page> and no navbar, so a member who reached it by URL got a
  // broken screen with NO back link and NO tabbar (a pushed route never has
  // one) — the same trap the notification center shipped with. The file is
  // marked for the deletion pass; only the route is gone.

  // Member-facing box totals screen. Surfaces the stage-aware estimatedTotal
  // from box creation (DRAFT) onward, and morphs into the invoice/receipt
  // view once SHIPPED+. Pushed over the tabbar — `meta.hideTabbar: true`
  // keeps focus on the flow.
  //
  // EAGER `component:` — Phase 1b push notifications make this URL a valid
  // cold-start landing point: a push tap for rows 5–9 (e.g.
  // `orderbox.shipped.invoice_ready`) deep-links straight to
  // `/orders/box/{id}/totals/` (contract Section 10). When the SW's openWindow
  // launches the app cold or the page-side OneSignal click handler runs
  // before the f7 router has mounted, this becomes the INITIAL URL — and
  // F7-Vue v9's <f7-view> render fn passes async functions directly to
  // Vue's h() on the initial page, stringifying the Promise as the literal
  // text "[object Promise]" until the dynamic import resolves (playbook
  // Section 5 row 16). Eager `component:` avoids that leak; guards (beforeEnter)
  // still fire correctly with eager component in F7 v9 (the v6/v7-era
  // "beforeEnter doesn't work with component:" warning does NOT apply —
  // process-route-queue.js runs guards before load() resolves the component).
  {
    path: '/orders/box/:boxId/totals/',
    id: 'order-box-totals',
    component: OrderBoxTotalsPage,
    beforeEnter: requireMembership,
    meta: {
      hideTabbar: true,
    },
  },

  // The carton itself, opened on the swiper page instead of the totals
  // breakdown — where the item sheet, cancel and «أنا عارف مين عنده» already
  // live (tips-wave P3-A, owner answer W-K). Four pushes land here:
  // `orderbox.item.out_of_stock`, `orderbox.item.suggestion_outcome` (any
  // variant that carries a carton), `orderbox.ready.missing_items` and
  // `orderbox.complete` (ship-ready pack: "your carton is complete — you can ask
  // to ship", route `/orders/box/{order_box_id}/`). An
  // unknown/missing boxId falls back to the ordinary list — OrdersPage only
  // narrows the swiper when it can find the id among her boxes.
  //
  // SAME component as the `/orders/` tab (imported eagerly above, for the
  // reason given there) — reached as a standalone pushed route rather than a
  // tab, so `meta.hideTabbar: true` and OrdersPage renders its own back link
  // for this one entry point.
  //
  // PLACEMENT: before the trailing-slash normalizer block below, matching
  // every other strict/eager route in this file (playbook Section 5 row 20).
  {
    path: '/orders/box/:boxId/',
    id: 'order-box-detail',
    component: OrdersPage,
    beforeEnter: requireMembership,
    meta: {
      hideTabbar: true,
    },
  },

  // ===== MEMBER-ANSWERABLE COLLECTION ISSUES (require auth + membership) =====
  //
  // The "قرارات مطلوبة" surface (Phase 2 of the member-answerable issues PRD).
  // /issues/ is the derived inbox hub; /issues/:issueId/ is the single-decision
  // card that the ItemIssueNeedsDecision notification deep-links to
  // (NotificationType::ItemIssueNeedsDecision → /issues/{item_issue_id}/) and
  // that the inbox rows + per-item badges open.
  //
  // EAGER `component:` on BOTH — a push tap / PWA cold-launch to /issues/{id}/
  // (or a refresh on the hub) can make either the boot URL; asyncComponent would
  // leak "[object Promise]" on first paint (playbook Section 5 row 16). Guards still
  // fire with eager component: on soft navigations (process-route-queue runs
  // beforeEnter before load()). The cold hard-load guard-skip (row 22) is covered
  // by App.vue's EAGER_MEMBER_REQUIRED_PREFIXES pre-empt (['/issues/']).
  //
  // PLACEMENT: before the trailing-slash normalizer block below, so the strict
  // /issues/ and /issues/:issueId/ patterns match the slashed URLs first; the
  // slashless /issues + /issues/:issueId normalizers (in that block) only catch
  // the no-slash variants (playbook Section 5 row 20). /issues/ (no param) and
  // /issues/:issueId/ (param required) don't overlap, so their order is free.
  {
    path: '/issues/',
    id: 'decisions-inbox',
    component: DecisionsInboxPage,
    beforeEnter: requireMembership,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },
  {
    path: '/issues/:issueId/',
    id: 'issue-decision',
    component: IssueDecisionPage,
    beforeEnter: requireMembership,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== POST DETAIL (require auth + membership) =====
  //
  // One post by id — the focusable deep-link target the backend asked for in
  // 2026-07-30-product-unavailable-fanout-notification.md and we replied to in
  // 2026-08-01-fe-post-detail-deeplink-and-notification-data.md. The
  // `product.unavailable` fan-out row currently routes to /home/, which drops the
  // member on a cursor-paginated feed to hunt for the product herself; once this
  // is on main the backend flips its route arm to /posts/{postId}/.
  //
  // EAGER `component:` — a push tap / PWA cold-launch can make this the boot URL;
  // asyncComponent would leak "[object Promise]" on first paint (playbook Section 5 row
  // 16). Guards still fire with eager component: in F7 v9 on soft navigations
  // (process-route-queue runs beforeEnter before load()). The cold hard-load
  // guard-skip (row 22) is covered by App.vue's EAGER_MEMBER_REQUIRED_PREFIXES
  // pre-empt, which lists '/posts/' alongside '/issues/'.
  //
  // PLACEMENT: before the trailing-slash normalizer block below, so the strict
  // /posts/:id/ pattern matches the slashed URL first; the slashless
  // /posts/:id normalizer (in that block, AFTER this route) only catches the
  // no-slash variant — getting that order backwards reproduces the row-20
  // loose-vs-strict RangeError loop.
  {
    path: '/posts/:id/',
    id: 'post-detail',
    component: PostDetailPage,
    beforeEnter: requireMembership,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== «تدبيسة النادي» — the club's shelf (require auth + membership) =====
  //
  // Only the listings with a piece still at the club (tadbeesa P3-A; BE→FE brief
  // 2026-09-23-tadbeesa-shelf-filter), pushed over the shell with no tabbar,
  // like a post's own page. Reached by tapping the header of any تدبيسة post
  // (HomePage, PostDetailPage) and — P3-B — the home shelf strip.
  //
  // EAGER `component:` — a reload makes this the boot URL (row 16). The cold
  // hard-load guard-skip (row 22) is covered by App.vue's
  // EAGER_MEMBER_REQUIRED_PREFIXES, which lists '/tadbeesa/'.
  //
  // PLACEMENT: before the trailing-slash normalizer block, so the strict
  // /tadbeesa/ pattern matches the slashed URL first; the slashless /tadbeesa
  // normalizer (in that block, AFTER this route) only catches the no-slash
  // variant (row 20).
  {
    path: '/tadbeesa/',
    id: 'tadbeesa',
    component: TadbeesaPage,
    beforeEnter: requireMembership,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== NOTIFICATION CENTER (club-neutral: require auth + a center) =====
  //
  // One center for every club (decision D5): requireNotificationsRoot, not
  // requireMembership, so a supplier reaches it from the settings helper, the
  // profile card and a content-free money push instead of bouncing to the
  // Wall. `meta.clubNeutral`: the URL names no club, so the club context
  // (useClubContext, the tabbars, the seller lifecycle banner) reads the
  // user's own club here instead of the path's default (sellers).
  //
  // These routes are guarded and NOT initial-URL eligible:
  // - A push tap for money rows (1–4) deep-links to /notifications/ which IS
  //   reachable cold, BUT the OneSignal click handler drains it via the
  //   PENDING_PUSH_ROUTE mechanism in App.vue after auth resolves, so the
  //   actual cold-start URL remains / (which BasePage handles eagerly).
  //   Only warm/rehydrated sessions navigate here directly.
  //   asyncComponent: is therefore safe here (not an initial-URL route).
  // - asyncComponent: matches the pattern used for all other guarded routes
  //   (/tg-launch/, /join/form/, etc.) in this file.
  {
    path: '/notifications/',
    id: 'notifications',
    asyncComponent: () => import('@/pages/NotificationCenterPage.vue'),
    beforeEnter: requireNotificationsRoot,
    meta: {
      hideTabbar: false,
      clubNeutral: true,
    },
  },
  {
    path: '/notifications/preferences/',
    id: 'notification-preferences',
    asyncComponent: () => import('@/pages/NotificationPreferencesPage.vue'),
    beforeEnter: requireNotificationsRoot,
    meta: {
      hideTabbar: false,
      clubNeutral: true,
    },
  },

  // ===== MEMBER REFERRAL (require auth + membership) =====
  //
  // The "invite & earn" screen (member-referral PWA surface, 2026-07-19), pushed
  // over the Profile row with `meta.hideTabbar: true`. STANDALONE top-level route
  // (NOT a member of any tabs:[] array) so it carries NO tab id — a referral tab
  // id would collide with the seller shell's tab stubs during a cross-shell swap
  // (playbook Section 5 rows 17/18).
  //
  // EAGER `component:` (import at the top): /referral/ is initial-URL eligible — a
  // member refreshing/bookmarking the screen makes it the cold-boot URL, and
  // asyncComponent would leak the literal "[object Promise]" on first paint
  // (playbook Section 5 row 16). Follows the /poola/ + OrderBoxTotalsPage precedent.
  // Guards still fire with eager component: in F7 v9 on soft navigations
  // (process-route-queue.js runs beforeEnter before load() resolves).
  //
  // The cold hard-load guard-skip (Section 5 row 22 — F7 takes the DOM-init path for an
  // eager component whose page renders an <f7-page>, skipping beforeEnter) is
  // covered by the App.vue EAGER_AUTH_REQUIRED_PATHS pre-empt added alongside
  // this route.
  //
  // PLACEMENT: before the trailing-slash normalizer block, so the strict
  // /referral/ pattern matches the slashed URL first; the slashless /referral
  // normalizer (in that block, AFTER this strict route) only catches the no-slash
  // variant — getting that order backwards reproduces the row-20 loose-vs-strict
  // RangeError loop.
  {
    path: '/referral/',
    id: 'referral',
    component: ReferralPage,
    beforeEnter: requireMembership,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== MY SUGGESTIONS (require auth + membership) =====
  //
  // «اقتراحاتي» — the shops she named for products nobody could source, and what
  // each earned (tip-reward P3-A). The referral screen's shape exactly: pushed
  // over a Profile row, standalone top-level route with NO tab id, eager
  // `component:` (import at the top), `meta.hideTabbar`. Its slash-less redirect
  // sits in the normalizer block below, AFTER this strict route (row 20), and the
  // cold hard-load guard-skip (row 22) is covered by EAGER_AUTH_REQUIRED_PATHS in
  // App.vue.
  {
    path: '/suggestions/',
    id: 'my-suggestions',
    component: MySuggestionsPage,
    beforeEnter: requireMembership,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== POOLA TEASER (require auth ONLY — pending members included) =====
  //
  // Standalone top-level route (NOT a member of the `/` tabs array). Surfaced via
  // tabbar links in both shells + a pending-landing banner, so it must reach all
  // three audiences: active sellers, active suppliers, AND pending/awaiting-
  // approval members. Hence beforeEnter: requireAuth (auth boundary only) —
  // requireMembership would bounce pending users to onboarding, defeating the
  // purpose. The page gates its per-user PoolShare data on hasActiveMembership
  // internally; the collective club total is Sanctum-auth, not membership-gated.
  //
  // EAGER `component:` (NOT asyncComponent), unlike /notifications/. /poola/ IS
  // initial-URL eligible in practice: a browser refresh or PWA reload while on the
  // page makes /poola/ the cold-boot URL. With asyncComponent, F7-Vue v9 leaks the
  // literal "[object Promise]" above the page on that initial render (playbook Section 5
  // row 16) — unacceptable on a headline marketing surface (empirically confirmed,
  // see tests/e2e/poola-route.spec.js). Eager `component:` avoids the leak.
  //
  // The trade-off (playbook Section 5 rows 16 vs 22): eager `component:` whose component
  // renders an <f7-page> has its beforeEnter SKIPPED on the initial hard-load (F7
  // takes the DOM-init path, not router.navigate). So requireAuth does NOT fire
  // for a cold hard-load — an UNAUTHENTICATED visitor is instead bounced by the
  // App.vue boot-time pre-empt (EAGER_AUTH_REQUIRED_PATHS), which rewrites /poola/
  // → /login/ before <f7-view> mounts. Same eager + boot-pre-empt pattern /login/
  // and /join/ use. Guards still fire on SOFT navigations here (tabbar/banner).
  {
    path: '/poola/',
    id: 'poola',
    component: PoolaPage,
    beforeEnter: requireAuthSellerPoola,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: false,
    },
  },

  // ===== «البولة اللي فاتت» — the last finished Poola (require auth ONLY) =====
  //
  // Pushed from the recap card on the Poola page's countdown screen
  // («شوف كل عروضها»): the last finished run, read-only, under a strip that keeps
  // the countdown to the next one on screen (poola-last-run P2-A). Same audience
  // and same guard as /poola/ — requireAuth, NOT requireMembership — because a
  // pending member or a visitor sees the card too.
  //
  // STANDALONE top-level route with NO tab id, `meta.hideTabbar: true` like every
  // other pushed page (/tadbeesa/, /posts/:id/).
  //
  // EAGER `component:` — a reload makes this the boot URL (row 16). The cold
  // hard-load guard skip (row 22) is covered by App.vue's
  // EAGER_AUTH_REQUIRED_PATHS, which lists it next to /poola/.
  //
  // PLACEMENT: right after /poola/. The strict /poola/ pattern does not match
  // /poola/last/ (the extra segment fails its end anchor), so the order between
  // the two is free; what matters is that the slashless /poola/last normalizer
  // sits in the bottom block, AFTER this strict route (row 20).
  {
    path: '/poola/last/',
    id: 'poola-last',
    component: PoolaLastRunPage,
    beforeEnter: requireAuthSellerPoola,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== TELEGRAM → BROWSER LAUNCH HANDOFF =====
  //
  // /tg-launch/ runs INSIDE the Telegram Mini App. It issues a single-use
  // launch token (POST /auth/launch-token/issue) and hands it to the system
  // browser via TmaAdapter.openExternalLink. Never reached as an initial URL,
  // so `asyncComponent:` is safe here. requireAuth, NOT requireMembership: a
  // collector with no club membership opens his day through this page («افتح
  // يومك» in his Telegram group) and a membership guard sent him to the
  // club-choice screen. Issuing the token needs a session, nothing more.
  //
  // /launch/ runs in the system/Chrome BROWSER. It reads ?t= from the URL,
  // exchanges it for a Sanctum session cookie (POST /auth/launch-token/exchange),
  // re-runs auth init (cookie-bootstrap, priority 3) to hydrate the store, then
  // navigates to /home/. No guard — the whole point is to authenticate users
  // who arrived cold without a session. EAGER `component:` is mandatory because
  // /launch is an initial-URL route; asyncComponent renders "[object Promise]"
  // before the dynamic import resolves (same F7-Vue v9 bug as /login/, /).
  {
    path: '/tg-launch/',
    id: 'tg-launch',
    asyncComponent: () => import('@/pages/TgLaunchPage.vue'),
    beforeEnter: requireAuth,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
      hideNavbar: true,
    },
  },
  {
    path: '/launch/',
    id: 'launch',
    component: LaunchPage,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
      hideNavbar: true,
    },
  },

  // ===== SUPPLIERS CLUB SHELL (require auth + membership + Suppliers-Club role) =====
  //
  // The supplier-side mirror of the seller `/` shell: a SECOND routable-tabs
  // shell (Opportunities · Feed · Profile) living in the SAME main <f7-view>
  // route table. «طلباتي» (Claims) is NOT its own tab — it's a segment of the
  // Opportunities window (suppliers-wall), and /suppliers/claims/ redirects to
  // /suppliers/?seg=claims (bottom block). This is the cold-boot-safe Option A
  // architecture — a push deep-link or PWA cold-launch to /suppliers/claims/
  // hits that redirect, lands on /suppliers/ under requireSupplier, and the SW
  // openWindow resolves it like every other deep-link we ship (architecture doc
  // Section 3/Section 4.2). Replaces the old /supplier/* (singular) bolt-on; /supplier/*
  // survives only as permanent redirect aliases in the bottom block.
  //
  // F7 invariants honored (playbook Section 5, architecture doc Section 5):
  //  - EAGER `component:` on the parent — /suppliers/* is initial-URL eligible
  //    (cold deep-link / login redirect), so `asyncComponent:` here would leak
  //    the literal "[object Promise]" on first paint (row 16). SupplierBasePage
  //    is a tiny shell; eager import is cheap. Guards still fire with eager
  //    `component:` in F7 v9 (process-route-queue runs guards before load()).
  //  - GLOBALLY-UNIQUE tab ids (suppliers-wall / suppliers-feed /
  //    suppliers-profile) so they can't collide with the seller shell's tab
  //    stubs during the cross-shell swap transition (rows 17/18, doc Section 5.1).
  //    NEVER reuse a bare 'home'/'feed' id here.
  //  - Tab CONTENT is `asyncComponent:` (code-split) — only the parent is eager.
  //  - PLACEMENT IS LOAD-BEARING: this strict block sits BEFORE the bottom
  //    trailing-slash redirects so /suppliers/ matches its strict tab route
  //    first (row 20). No `alias:` on tab defs (row 21).
  //  - Tab paths are parent-relative: F7 flattens them as
  //    `${parentPath}/${tabPath}` (router-class.js flattenRoutes), so '/',
  //    '/claims/', '/profile/' under '/suppliers/' resolve to /suppliers/,
  //    /suppliers/claims/, /suppliers/profile/.
  {
    path: '/suppliers/',
    // master: true — COLD-LOAD ORPHAN FIX (playbook Section 5 row 24 + row 17).
    // Identical rationale to the '/' seller shell above. SupplierBasePage
    // renders NO <f7-page> (row 17). Without master:true, a cold hard-load of
    // /suppliers/* causes get-router-initial-component.js to pre-render this
    // eager component → router.init() finds no .page child → navigate() mounts
    // it again → orphan tabbar with duplicate id="suppliers-wall". master:true
    // suppresses the pre-render; all master-detail side-effects are inert at
    // masterDetailBreakpoint=0. Guards fire via the preserved navigate() path.
    // flattenRoutes inherits master:true onto all supplier tab routes, covering
    // cold loads of /suppliers/feed/, /suppliers/profile/ too.
    master: true,
    component: SupplierBasePage,
    beforeEnter: requireSupplier,
    options: {
      transition: 'f7-fade',
    },
    tabs: [
      // First (default) tab shares the shell URL (/suppliers/) — the Wall.
      // PRIMARY surface is the POSTS-GROUPED feed (SupplierWallPostsPage, P8
      // posts-base pivot). The old flat per-item page (SupplierWallPage.vue) is
      // now SUPERSEDED and dead — its drill-down role moved to
      // SupplierWallPostDetailPage; mark SupplierWallPage.vue for the cleanup
      // pass (do not delete inline per CLAUDE.md "Agent working rules"). Tab
      // content stays `asyncComponent:` (code-split); only the shell is eager.
      {
        path: '/',
        id: 'suppliers-wall',
        asyncComponent: () => import('@/pages/SupplierWallPostsPage.vue'),
        beforeEnter: requireSupplier,
        options: {
          transition: 'f7-fade',
          clearPreviousHistory: false,
        },
        meta: {
          hideTabbar: false,
        },
      },
      // «اليوميات» (Daily Orders) tab (supplier-daily-orders design spec
      // 2026-07-05, Section 2). Placed AFTER the Wall (landing) tab, BEFORE the Feed.
      // Parent-relative path '/orders/' flattens to /suppliers/orders/ (F7
      // flattenRoutes joins `${parentPath}/${tabPath}` then collapses '//'), so
      // it does NOT collide with the seller `/orders/` tab (nested under '/').
      // Globally-unique id `suppliers-orders` — NEVER bare 'orders' (the seller
      // shell already owns that <f7-tab id>; a collision would break the
      // cross-shell #id DOM resolve, playbook Section 5 rows 17/18/24). `master:true` is
      // inherited from the parent /suppliers/ route via flattenRoutes extend() —
      // do NOT re-add it. Tab content is `asyncComponent:` (code-split) like its
      // siblings; only the shell parent is eager.
      {
        path: '/orders/',
        id: 'suppliers-orders',
        asyncComponent: () => import('@/pages/SupplierDailyOrdersPage.vue'),
        beforeEnter: requireSupplier,
        options: {
          transition: 'f7-fade',
        },
        meta: {
          hideTabbar: false,
        },
      },
      // Content feed (supplier-content-feed plan 2026-06-26, C1). The
      // sellers-club posts feed + seller-interest segment; Wall stays the
      // landing tab. Parent-relative path '/feed/' flattens to /suppliers/feed/.
      // Globally-unique id `suppliers-feed`. Tab content is `asyncComponent:`
      // (code-split), like its siblings — only the shell parent is eager.
      {
        path: '/feed/',
        id: 'suppliers-feed',
        asyncComponent: () => import('@/pages/SupplierFeedPage.vue'),
        beforeEnter: requireSupplier,
        options: {
          transition: 'f7-fade',
        },
        meta: {
          hideTabbar: false,
        },
      },
      // NOTE: there is no `suppliers-claims` tab. «طلباتي» (Claims) was merged
      // into the «الفرص» window as a segment (supplier-opportunities-merged-window
      // plan, 2026-06-27). /suppliers/claims/ now redirects to /suppliers/?seg=claims
      // (trailing-slash normalizer block below) — the window preselects طلباتي.
      // The shop's wallet: the seller page, a tab with no tab-bar link (the bar holds
      // five), entered from his profile, his monthly statement and the deposit message.
      // Routable tab = cold-load safe with asyncComponent, as the seller's /wallet/.
      {
        path: '/wallet/',
        id: 'suppliers-wallet',
        asyncComponent: () => import('@/pages/WalletPage.vue'),
        beforeEnter: requireSupplier,
        meta: {
          hideTabbar: false,
          transition: 'f7-fade',
        },
      },
      {
        path: '/profile/',
        id: 'suppliers-profile',
        asyncComponent: () => import('@/pages/ProfilePage.vue'),
        beforeEnter: requireSupplier,
        meta: {
          hideTabbar: false,
          transition: 'f7-fade',
        },
      },
    ],
  },

  // NOTE: the per-post drill-down is NOT a route — it opens as an <f7-popup>
  // overlay from SupplierWallPostsPage (mirroring the seller add-to-cart popup),
  // so the wall page is never unmounted and its scroll/feed survive. No
  // /suppliers/post/:postId/ route + no detail normalizer needed.

  // ===== SUPPLIERS POOLA (require auth ONLY — pending suppliers included) =====
  //
  // Supplier-namespaced mirror of /poola/. Placing the Poola teaser under
  // /suppliers/ makes clubTypeForPath('/suppliers/poola/') resolve to
  // 'suppliers', which atomically fixes all three symptoms for supplier users:
  //   1. clubTypeForPath → 'suppliers' → showSupplierTabbar = true (supplier
  //      tabbar shown via AppLayout.provide('showSupplierTabbar', …)).
  //   2. isSellerShell = false → <membership-lifecycle-banner> hidden.
  //   3. Real supplier URL in the address bar and history.
  //
  // STANDALONE top-level route (NOT inside the /suppliers/ shell's tabs:[]):
  // it must NOT carry a tab id (would collide with the shell's globally-unique
  // suppliers-wall / suppliers-feed / suppliers-profile ids — architecture
  // doc Section 5.1 + playbook Section 5 rows 17/18). Same guard as /poola/ (requireAuth,
  // NOT requireSupplier) so pending members in the supplier queue can also
  // reach the teaser.
  //
  // EAGER `component:` (NOT asyncComponent): /suppliers/poola/ is initial-URL
  // eligible — a supplier refresh/bookmark while on the teaser makes this the
  // cold-boot URL. asyncComponent would leak "[object Promise]" on that first
  // paint (playbook Section 5 row 16). Guards (beforeEnter) still fire correctly with
  // eager component: in F7 v9 on soft navigations (process-route-queue.js runs
  // guards before load()). Cold hard-load guard-skip (Section 5 row 22) is covered by
  // the App.vue EAGER_AUTH_REQUIRED_PATHS pre-empt added alongside this route.
  //
  // PLACEMENT: after the /suppliers/ shell block, before the trailing-slash
  // normalizer section. This ensures /suppliers/ matches its strict tab route
  // first (row 20); the new /suppliers/poola/ strict route matches before its
  // own normalizer below. No /suppliers/(.*) catch-all exists, so no shadow
  // risk — and the /suppliers/claims/ redirect (bottom block) only matches the
  // exact deep-link path, never /suppliers/ itself.
  {
    path: '/suppliers/poola/',
    id: 'suppliers-poola',
    component: PoolaPage,
    beforeEnter: requireAuth,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: false,
    },
  },

  // Supplier-namespaced mirror of /poola/last/ (see that route for the why). Under
  // /suppliers/ so the page it goes back to is /suppliers/poola/ and
  // clubTypeForPath keeps resolving to 'suppliers' through the round trip. Same
  // component, same guard, eager for the same reason; its own globally-unique id,
  // no tab id. Its slashless normalizer is in the supplier cluster of the bottom
  // block, AFTER this strict route (row 20).
  {
    path: '/suppliers/poola/last/',
    id: 'suppliers-poola-last',
    component: PoolaLastRunPage,
    beforeEnter: requireAuth,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== SUPPLIER ORDER DETAIL (require auth + membership + Suppliers-Club role) =====
  //
  // The dedicated per-order detail route reached by tapping an order card on
  // /suppliers/orders/ (box-first design spec 2026-07-07 Section 3.2). One box's items +
  // the invoice/source-post sheets. STANDALONE top-level route (NOT a tab of the
  // /suppliers/ shell), so it carries NO tab id — pushed over the shell with
  // `meta.hideTabbar: true`.
  //
  // Route segment stays `box` (matches the backend `supplier-boxes` entity + the
  // shipped sibling /orders/box/:boxId/totals/) even though the UI label is
  // «اوردر»; URL segments mirror entities, not copy.
  //
  // EAGER `component:` (import at the top): a push / PWA cold-launch deep-link can
  // make this the boot URL, and asyncComponent would paint "[object Promise]" on
  // the initial render (playbook Section 5 row 16) — same reasoning as the order-box-totals
  // route above. Guards (beforeEnter) still fire with eager component: in F7 v9 on
  // soft navigations (process-route-queue.js runs guards before load()). The cold
  // hard-load guard-skip (Section 5 row 22) is covered by the App.vue
  // EAGER_AUTH_REQUIRED_PREFIXES pre-empt (['/suppliers/box/']).
  //
  // PLACEMENT: after the /suppliers/ shell + /suppliers/poola/, BEFORE the
  // trailing-slash normalizer block, so the strict /suppliers/box/:boxId/ pattern
  // matches the slashed URL first; the slashless /suppliers/box/:boxId normalizer
  // (in the supplier cluster below, AFTER this strict route) only catches the
  // no-slash variant — getting that order backwards reproduces the row-20
  // loose-vs-strict RangeError loop.
  {
    path: '/suppliers/box/:boxId/',
    id: 'supplier-box-detail',
    component: SupplierBoxDetailPage,
    beforeEnter: requireSupplier,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== THE SHOP'S INVITE SCREEN (require auth + membership + a supplier stage) =====
  //
  // The supplier twin of /referral/ (owner decision 2 Nov 2026): the SAME
  // ReferralPage, pushed over the supplier profile row, reads his shop's code and
  // the sellers who joined through it; under /suppliers/ the page hides every
  // money element (a shop earns nothing from it). Standalone top-level route,
  // NO tab id (rows 17/18). EAGER `component:` — a reload makes it the boot URL,
  // and asyncComponent would paint "[object Promise]" first (row 16). The cold
  // hard-load guard-skip (row 22) is covered by bootPreempts.js
  // `supplierRoutePreempt` (EAGER_AUTH_REQUIRED_PREFIXES), all four tiers.
  //
  // PLACEMENT: BEFORE the trailing-slash normalizer block, so the strict
  // /suppliers/referral/ pattern matches the slashed URL first (row 20).
  {
    path: '/suppliers/referral/',
    id: 'suppliers-referral',
    component: ReferralPage,
    beforeEnter: requireSupplier,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },

  // ===== THE COLLECTOR'S DAY (require auth + the collector capability) =====
  //
  // Staff surface for a collector on a collection plan (collector-app phase 1):
  // «يومك» at /collector/ (the plans he collects on today, his cash, his shops
  // in the leader's route order) and «المحل» at /collector/shops/:supplierId/
  // (one shop's lines and the marks). NOT a member club: requireCollector skips
  // the membership tier, and clubTypeForPath gives /collector/* its own type so
  // neither member tabbar nor the seller lifecycle banner shows over it.
  //
  //  - EAGER `component:` on both (see the import comment at the top): a reload
  //    makes either the boot URL (row 16). The cold hard-load guard skip (row
  //    22) is covered by App.vue's EAGER_COLLECTOR_PREFIX pre-empt.
  //  - `:supplierId` is the shop's organization id from the day read; the one
  //    shop the backend sends with `supplierId: null` (lines with no shop) is
  //    addressed as `none`. `?plan=<id>` picks the plan when two batches run.
  //  - PLACEMENT: strict routes here, BEFORE the trailing-slash normalizer block
  //    (row 20); their slashless normalizers sit in that block, after them.
  {
    path: '/collector/',
    id: 'collector-day',
    component: CollectorDayPage,
    beforeEnter: requireCollector,
    options: {
      transition: 'f7-fade',
    },
    meta: {
      hideTabbar: true,
    },
  },
  {
    path: '/collector/shops/:supplierId/',
    id: 'collector-shop',
    component: CollectorShopPage,
    beforeEnter: requireCollector,
    meta: {
      hideTabbar: true,
    },
  },

  // Trailing-slash normalizers for tab URLs.
  //
  // Why these are placed HERE (after all real routes, before the 404):
  // F7 uses path-to-regexp v6 which produces an *asymmetric* matcher —
  //   pathToRegexp('/wallet')  → /^\/wallet[\/#\?]?$/i   (matches BOTH
  //                                                       /wallet AND /wallet/)
  //   pathToRegexp('/wallet/') → /^\/wallet\/[\/#\?]?$/i (strict, /wallet/ only)
  // findMatchingRoute (router-class.js:376) iterates flattened routes in
  // order and uses the FIRST match. Placing these redirects BEFORE the
  // /wallet/ tab route would catch /wallet/ via the loose pattern and
  // redirect → match → redirect → infinite loop (`RangeError: Maximum
  // call stack size exceeded` in F7View's mounted hook). Placing them
  // AFTER means the strict tab patterns match /wallet/ first; only
  // /wallet (no slash) falls through to these and gets normalized.
  { path: '/wallet', redirect: '/wallet/' },
  { path: '/orders', redirect: '/orders/' },
  { path: '/profile', redirect: '/profile/' },
  { path: '/tg-launch', redirect: '/tg-launch/' },
  { path: '/launch', redirect: '/launch/' },
  { path: '/notifications', redirect: '/notifications/' },
  { path: '/notifications/preferences', redirect: '/notifications/preferences/' },
  { path: '/poola', redirect: '/poola/' },
  // The last-Poola page. AFTER its strict /poola/last/ route (row 20). The loose
  // /poola matcher above ends at /poola or /poola/, so it cannot catch this.
  { path: '/poola/last', redirect: '/poola/last/' },
  { path: '/referral', redirect: '/referral/' },
  { path: '/suggestions', redirect: '/suggestions/' },
  // Member-answerable issues. Same asymmetric-matcher rule (playbook Section 5 row 20):
  // placed AFTER the strict /issues/ and /issues/:issueId/ routes so the loose
  // patterns can't catch the slashed URLs and infinite-loop. The param normalizer
  // MUST be the FUNCTION form — a redirect STRING is used verbatim (F7 does not
  // interpolate :issueId), so return the slashed URL from the matched context.
  { path: '/issues', redirect: '/issues/' },
  {
    path: '/issues/:issueId',
    redirect: ({ to }) => `/issues/${to.params.issueId}/`,
  },
  // Post detail. Same asymmetric-matcher rule (playbook Section 5 row 20): placed AFTER
  // the strict /posts/:id/ route so the loose param pattern can't catch the
  // slashed URL and infinite-loop. FUNCTION form, not a redirect STRING — F7 does
  // not interpolate `:id` and would send the member to a literal "/posts/:id/".
  {
    path: '/posts/:id',
    redirect: ({ to }) => `/posts/${to.params.id}/`,
  },
  // «تدبيسة» shelf. Same asymmetric-matcher rule (playbook Section 5 row 20):
  // AFTER the strict /tadbeesa/ route, so the loose pattern can't catch the
  // slashed URL and loop. No param, so the plain STRING form is fine here.
  { path: '/tadbeesa', redirect: '/tadbeesa/' },

  // Onboarding (/join/) trailing-slash normalizers. Same asymmetric-matcher rule
  // (playbook Section 5 row 20): placed AFTER the strict /join/, /join/form/,
  // /join/review/ routes so the loose patterns can't catch the slashed URLs and
  // infinite-loop. The slashless `/join` matcher is /^\/join[\/#\?]?$/i — it
  // matches /join and /join/ but NOT /join/form/ or /join/review/ (the extra
  // path segment fails the end-anchor), so it can't shadow the deeper routes.
  // More-specific paths first, defensively.
  { path: '/join/gains', redirect: '/join/gains/' },
  { path: '/join/form', redirect: '/join/form/' },
  { path: '/join/review', redirect: '/join/review/' },
  { path: '/join', redirect: ONBOARDING_ENTRY },

  // Suppliers-Club shell trailing-slash normalizers. Same asymmetric-matcher
  // rule as the seller tabs above — placed AFTER the strict /suppliers/ tab
  // routes so the loose patterns can't catch the slashed URLs and infinite-loop
  // (playbook Section 5 row 20). The strict /suppliers/, /suppliers/feed/,
  // /suppliers/profile/, /suppliers/poola/ match first; only the slashless
  // variants fall through.
  { path: '/suppliers', redirect: '/suppliers/' },
  { path: '/suppliers/orders', redirect: '/suppliers/orders/' },
  { path: '/suppliers/feed', redirect: '/suppliers/feed/' },
  { path: '/suppliers/profile', redirect: '/suppliers/profile/' },
  { path: '/suppliers/wallet', redirect: '/suppliers/wallet/' },
  { path: '/suppliers/referral', redirect: '/suppliers/referral/' },
  { path: '/suppliers/poola', redirect: '/suppliers/poola/' },
  { path: '/suppliers/poola/last', redirect: '/suppliers/poola/last/' },
  // Slashless normalizer for the PARAM detail route. Placed AFTER the strict
  // /suppliers/box/:boxId/ route above (playbook Section 5 row 20 — the loose param
  // pattern matches BOTH slash forms, so it must come after the strict one or it
  // catches the slashed URL and infinite-loops). A redirect STRING would be used
  // verbatim (F7 does NOT interpolate `:boxId` into it — framework7/modules/router/
  // redirect.js), sending the user to a literal "/suppliers/box/:boxId/". Use the
  // FUNCTION form: F7 calls it with the matched-route context `{ to, resolve, ... }`
  // (no `from`), and `to.params.boxId` is already extracted. Return the slashed URL.
  {
    path: '/suppliers/box/:boxId',
    redirect: ({ to }) => `/suppliers/box/${to.params.boxId}/`,
  },

  // Collector trailing-slash normalizers. Same asymmetric-matcher rule (playbook
  // Section 5 row 20): AFTER the strict /collector/ and /collector/shops/:supplierId/
  // routes. The shop normalizer is the FUNCTION form (a redirect STRING is used
  // verbatim — F7 does not interpolate `:supplierId`) and keeps the `?plan=` query.
  { path: '/collector', redirect: '/collector/' },
  {
    path: '/collector/shops/:supplierId',
    redirect: ({ to }) => {
      const query = typeof to.url === 'string' && to.url.includes('?') ? to.url.slice(to.url.indexOf('?')) : '';
      return `/collector/shops/${to.params.supplierId}/${query}`;
    },
  },

  // «طلباتي» (Claims) deep-link → Opportunities window, طلباتي segment.
  // /suppliers/claims/ is a LIVE push deep-link; after the Claims tab merged
  // into the «الفرص» window (supplier-opportunities-merged-window plan,
  // 2026-06-27) it must keep resolving — now to /suppliers/?seg=claims, which
  // the window reads on mount (f7route.query) to preselect طلباتي. NOT a
  // trailing-slash normalizer: it's a different-path redirect, so the
  // loose-vs-strict loop trap (row 20) does not apply. Both variants point
  // straight at the destination (single hop, no chaining through a removed tab).
  { path: '/suppliers/claims/', redirect: '/suppliers/?seg=claims' },
  { path: '/suppliers/claims', redirect: '/suppliers/?seg=claims' },

  // PERMANENT /supplier (singular) → /suppliers (plural) aliases. The old
  // /supplier/* bolt-on shipped a LIVE push deep-link (/supplier/claims/, drained
  // by App.vue's PENDING_PUSH_ROUTE via isSafeOriginRoute) and user bookmarks;
  // keep them resolving after the shell move. /supplier/wall/ → the shell root
  // (Wall is the default tab); /supplier/claims/ → the merged Opportunities
  // window's طلباتي segment (/suppliers/?seg=claims), same destination as the
  // new /suppliers/claims/ redirect above (single hop — don't chain through the
  // removed tab URL). Strict (slashed) variants first, then the loose `/supplier`
  // which covers both /supplier and /supplier/. None of these can catch
  // /suppliers* (the trailing `s` fails the `/supplier` matcher), so order vs the
  // normalizers above is safe.
  { path: '/supplier/claims/', redirect: '/suppliers/?seg=claims' },
  { path: '/supplier/claims', redirect: '/suppliers/?seg=claims' },
  { path: '/supplier/wall/', redirect: '/suppliers/' },
  { path: '/supplier/wall', redirect: '/suppliers/' },
  { path: '/supplier', redirect: '/suppliers/' },

  // ===== 404 =====
  {
    path: '(.*)',
    component: NotFoundPage,
    meta: {
      hideTabbar: true,
    },
  },
];
